import React from 'react'

import { STACK, VIEW, FORMAT } from 'types/deck'

import { useAppSelector, useActionless } from 'redux/hooks'
import { SET_CACHEABLES_STATE } from 'redux/cacheables/actions/types'

import PhatButton from 'components/formElements/PhatButton'
import ArchidektDropdown from 'components/elements/ArchidektDropdown'
import ToastService from 'services/toast.service'
import Icon from 'components/elements/Icon'

import styles from './viewExtras.module.scss'

type Props = {}

const ViewExtras = ({}: Props) => {
  const imageSize = useAppSelector(state => state.cacheables.imageSize)
  const disableMargins = useAppSelector(state => state.cacheables.disableMargins)
  const showEmptyCardStacks = useAppSelector(state => state.cacheables.showEmptyCardStacks)
  const hideDefaultTypeCategories = useAppSelector(state => state.cacheables.hideDefaultTypeCategories)
  const textColumns = useAppSelector(state => state.cacheables.textColumns)
  const pinnedCategory = useAppSelector(state => state.cacheables.pinnedCategory)
  const draggableStacks = useAppSelector(state => state.cacheables.draggableStacks)
  const hideOutOfDeckCategories = useAppSelector(state => state.cacheables.hideOutOfDeckCategories)
  const view = useAppSelector(state => state.deck.view)
  const stack = useAppSelector(state => state.deck.stack)
  const deckFormat = useAppSelector(state => state.deck.format)

  const handleUpdateTextColumns = (newState: Partial<typeof textColumns>) => {
    setCacheableState({ textColumns: { ...textColumns, ...newState } })
  }

  const [setCacheableState] = useActionless(SET_CACHEABLES_STATE)

  return (
    <ArchidektDropdown
      menuClassName={styles.menu}
      options={[
        {
          type: 'extras',
          id: 'card-size-picker',
          label: 'Card image size',
          icon: 'image outline',
          hidden: view !== VIEW.CARD && view !== VIEW.GRID,
          options: [
            {
              label: 'Small',
              icon: imageSize === 1 ? 'check circle outline' : 'circle outline',
              onClick: () => setCacheableState({ imageSize: 1 }),
            },
            {
              label: 'Normal',
              icon: imageSize === 2 ? 'check circle outline' : 'circle outline',
              onClick: () => setCacheableState({ imageSize: 2 }),
            },
            {
              label: 'Large',
              icon: imageSize === 3 ? 'check circle outline' : 'circle outline',
              onClick: () => setCacheableState({ imageSize: 3 }),
            },
          ],
        },
        {
          label: 'Enabled columns',
          icon: 'columns',
          type: 'extras',
          id: 'text-view-columns',
          hidden: view !== VIEW.TEXT,
          options: [
            {
              type: 'checkbox',
              checked: textColumns.extraOptions,
              label: 'Extra Options',
              onChange: () => handleUpdateTextColumns({ extraOptions: !textColumns.extraOptions }),
            },
            {
              type: 'checkbox',
              checked: textColumns.dragHandle,
              label: 'Drag Handle',
              onChange: () => handleUpdateTextColumns({ dragHandle: !textColumns.dragHandle }),
            },
            {
              type: 'checkbox',
              checked: textColumns.finishes,
              label: 'Finishes',
              onChange: () => handleUpdateTextColumns({ finishes: !textColumns.finishes }),
            },
            {
              type: 'checkbox',
              checked: textColumns.manaCost,
              label: 'Mana Cost',
              onChange: () => handleUpdateTextColumns({ manaCost: !textColumns.manaCost }),
            },
            {
              type: 'checkbox',
              checked: textColumns.setSymbol,
              label: 'Set Symbol',
              onChange: () => handleUpdateTextColumns({ setSymbol: !textColumns.setSymbol }),
            },
            {
              type: 'checkbox',
              checked: textColumns.price,
              label: 'Price',
              onChange: () => handleUpdateTextColumns({ price: !textColumns.price }),
            },
            {
              type: 'checkbox',
              checked: textColumns.collectionInfo,
              label: 'Collection info',
              onChange: () => handleUpdateTextColumns({ collectionInfo: !textColumns.collectionInfo }),
            },
            {
              type: 'checkbox',
              checked: textColumns.colorTag,
              label: 'Color tag',
              onChange: () => handleUpdateTextColumns({ colorTag: !textColumns.colorTag }),
            },
            {
              hidden: deckFormat !== FORMAT.EDH,
              type: 'checkbox',
              checked: textColumns.gamechangers,
              label: 'Game Changer',
              onChange: () => handleUpdateTextColumns({ gamechangers: !textColumns.gamechangers }),
            },
            {
              hidden: deckFormat !== FORMAT.CANLANDER,
              type: 'checkbox',
              checked: textColumns.canlanderPoints,
              label: 'Canlander Points',
              onChange: () => handleUpdateTextColumns({ canlanderPoints: !textColumns.canlanderPoints }),
            },
          ],
        },
        { type: 'spacer', hidden: view === VIEW.SPREADSHEET },
        {
          label: 'Disable margins',
          type: 'checkbox',
          checked: disableMargins,
          hidden: view === VIEW.SPREADSHEET,
          onChange: () => {
            setCacheableState({ disableMargins: !disableMargins })

            if (window.innerWidth < 2300 && !disableMargins)
              ToastService.create(
                'This option does nothing for screens less than 2300 pixels wide',
                'Deck options',
                'warning',
              )
          },
        },
        {
          label: 'Draggable categories',
          type: 'checkbox',
          checked: draggableStacks,
          onChange: () => setCacheableState({ draggableStacks: !draggableStacks }),
          hidden: view !== VIEW.CARD && view !== VIEW.TEXT,
          disabled: stack !== STACK.MULTIPLE && stack !== STACK.CUSTOM,
        },
        {
          label: 'Pinned category',
          type: 'checkbox',
          checked: pinnedCategory,
          onChange: () => setCacheableState({ pinnedCategory: !pinnedCategory }),
        },
        { type: 'spacer' },
        {
          type: 'extras',
          id: 'empty-category-options',
          label: 'Category view options',
          options: [
            {
              label: 'Show empty categories',
              type: 'checkbox',
              checked: showEmptyCardStacks,
              onChange: () => setCacheableState({ showEmptyCardStacks: !showEmptyCardStacks }),
            },
            {
              label: 'Hide default categories',
              type: 'checkbox',
              checked: hideDefaultTypeCategories,
              disabled: !showEmptyCardStacks,
              onChange: () => setCacheableState({ hideDefaultTypeCategories: !hideDefaultTypeCategories }),
            },
            {
              label: 'Hide Maybeboard(s)',
              type: 'checkbox',
              checked: hideOutOfDeckCategories,
              onChange: () => setCacheableState({ hideOutOfDeckCategories: !hideOutOfDeckCategories }),
            },
          ],
        },
      ]}>
      <PhatButton className={styles.trigger}>
        <Icon name="cog" />
      </PhatButton>
    </ArchidektDropdown>
  )
}

export default ViewExtras
